import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Snackbar,
  Alert,
  Paper,
  Tabs,
  Tab,
  TextField,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LinkIcon from '@mui/icons-material/Link';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { SavedCanvaAsset, CanvaAssetCategory } from '../services/canvaIntegrationService';
import { nanoid } from 'nanoid';
import { db, storage } from '../firebase/config';
import { collection, addDoc } from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';

interface CanvaEmbeddedPickerProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  category: CanvaAssetCategory;
  relatedId?: string;
  onDesignImported: (asset: SavedCanvaAsset) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`canva-import-tabpanel-${index}`}
      aria-labelledby={`canva-import-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Input = styled('input')({
  display: 'none',
});

const CanvaEmbeddedPicker: React.FC<CanvaEmbeddedPickerProps> = ({
  open,
  onClose,
  organizationId,
  category,
  relatedId,
  onDesignImported
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [designTitle, setDesignTitle] = useState('');
  const [canvaUrl, setCanvaUrl] = useState('');
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCanvaUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCanvaUrl(event.target.value);
  };

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDesignTitle(event.target.value);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setImageFile(file);
      
      // Preview the image
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setUploadedImage(e.target.result as string);
          
          // Auto-generate a title if none exists
          if (!designTitle) {
            setDesignTitle(file.name.split('.')[0] || 'Canva Design');
          }
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setDesignTitle('');
    setCanvaUrl('');
    setUploadedImage(null);
    setImageFile(null);
    setIsLoading(false);
  };

  const handleImportFromUrl = async () => {
    if (!canvaUrl) {
      setError('Please enter a Canva design URL');
      return;
    }

    if (!designTitle) {
      setError('Please enter a title for your design');
      return;
    }

    try {
      setIsLoading(true);

      // Create a mock thumbnail for now (we could implement a thumbnail fetcher if needed)
      const thumbnailUrl = 'https://static.canva.com/static/images/canva_logo_social_media.jpg';
      
      const assetId = nanoid();
      const now = new Date().toISOString();
      
      // Create the asset object
      const asset: SavedCanvaAsset = {
        id: assetId,
        designId: `canva-${Date.now()}`,
        title: designTitle,
        description: 'Imported from Canva URL',
        category,
        imageUrl: thumbnailUrl, // We'll use a placeholder for now
        thumbnailUrl,
        canvaUrl,
        dimensions: {
          width: 1200,
          height: 630
        },
        orgId: organizationId,
        createdAt: now,
        updatedAt: now
      };

      // Add related ID if provided
      if (relatedId) {
        if (category === 'event') {
          asset.eventId = relatedId;
        } else if (category === 'trip') {
          asset.tripId = relatedId;
        } else if (category === 'membership') {
          asset.membershipId = relatedId;
        }
      }

      // Save to Firestore
      await addDoc(collection(db, 'organizations', organizationId, 'canvaAssets'), asset);
      
      setSuccessMessage('Design imported successfully!');
      onDesignImported(asset);
      
      // Reset form and close dialog after a short delay
      setTimeout(() => {
        resetForm();
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error importing design from URL:', err);
      setError('Failed to import design');
      setIsLoading(false);
    }
  };

  const handleImportFromFile = async () => {
    if (!imageFile || !uploadedImage) {
      setError('Please upload an image file');
      return;
    }

    if (!designTitle) {
      setError('Please enter a title for your design');
      return;
    }

    try {
      setIsLoading(true);

      // Generate unique IDs
      const assetId = nanoid();
      const storageRef = ref(storage, `organizations/${organizationId}/canvaAssets/${assetId}`);
      
      // Upload the image to Firebase Storage
      await uploadString(storageRef, uploadedImage, 'data_url');
      
      // Get the download URL
      const imageUrl = await getDownloadURL(storageRef);
      
      const now = new Date().toISOString();
      
      // Create the asset object
      const asset: SavedCanvaAsset = {
        id: assetId,
        designId: `canva-${Date.now()}`,
        title: designTitle,
        description: 'Imported from local file',
        category,
        imageUrl,
        thumbnailUrl: imageUrl,
        canvaUrl: '', // No Canva URL for uploads
        dimensions: {
          width: 1200,
          height: 630
        },
        orgId: organizationId,
        createdAt: now,
        updatedAt: now
      };

      // Add related ID if provided
      if (relatedId) {
        if (category === 'event') {
          asset.eventId = relatedId;
        } else if (category === 'trip') {
          asset.tripId = relatedId;
        } else if (category === 'membership') {
          asset.membershipId = relatedId;
        }
      }

      // Save to Firestore
      await addDoc(collection(db, 'organizations', organizationId, 'canvaAssets'), asset);
      
      setSuccessMessage('Design imported successfully!');
      onDesignImported(asset);
      
      // Reset form and close dialog after a short delay
      setTimeout(() => {
        resetForm();
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error importing design from file:', err);
      setError('Failed to import design');
      setIsLoading(false);
    }
  };

  const handleCreateInCanva = () => {
    // Open Canva in a new tab for creation
    window.open('https://www.canva.com/create/social-media/', '_blank');
    setSuccessMessage('Canva opened in a new tab. After creating your design, download it and upload it here.');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle>
        <Typography variant="h6">Import from Canva</Typography>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="canva import methods"
          sx={{ mt: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<CloudUploadIcon />} label="Upload Design" />
          <Tab icon={<LinkIcon />} label="Link Design" />
        </Tabs>
      </DialogTitle>
      
      <DialogContent>
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ textAlign: 'center', py: 1 }}>
            <Typography variant="h5" gutterBottom>
              Upload Your Design
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <TextField
                label="Design Title"
                variant="outlined"
                fullWidth
                value={designTitle}
                onChange={handleTitleChange}
                margin="normal"
              />
            </Box>
            
            <Paper 
              elevation={3} 
              sx={{ 
                p: 4, 
                maxWidth: 600, 
                margin: '20px auto',
                bgcolor: 'background.paper',
                borderRadius: 2
              }}
            >
              {uploadedImage ? (
                <Box sx={{ mb: 2 }}>
                  <img 
                    src={uploadedImage} 
                    alt="Selected design" 
                    style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }} 
                  />
                </Box>
              ) : (
                <Typography variant="body1" paragraph>
                  Upload your Canva design (PNG, JPG, or PDF):
                </Typography>
              )}
              
              <label htmlFor="contained-button-file">
                <Input 
                  accept="image/*,.pdf" 
                  id="contained-button-file" 
                  type="file" 
                  onChange={handleFileChange}
                />
                <Button
                  variant="contained"
                  component="span"
                  sx={{ mt: 1 }}
                >
                  Choose File
                </Button>
              </label>
              
              {uploadedImage && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleImportFromFile}
                  sx={{ ml: 2, mt: 1 }}
                  disabled={isLoading}
                >
                  {isLoading ? 'Importing...' : 'Import Design'}
                </Button>
              )}
            </Paper>
            
            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" color="text.secondary">OR</Typography>
            </Divider>
            
            <Button
              variant="outlined"
              color="primary"
              onClick={handleCreateInCanva}
              sx={{ mt: 2 }}
            >
              Create New In Canva
            </Button>
            
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              This will open Canva in a new tab. After creating, download your design and upload it here.
            </Typography>
          </Box>
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ textAlign: 'center', py: 1 }}>
            <Typography variant="h5" gutterBottom>
              Link Existing Canva Design
            </Typography>
            
            <Paper 
              elevation={3} 
              sx={{ 
                p: 4, 
                maxWidth: 600, 
                margin: '20px auto',
                bgcolor: 'background.paper',
                borderRadius: 2
              }}
            >
              <TextField
                label="Design Title"
                variant="outlined"
                fullWidth
                value={designTitle}
                onChange={handleTitleChange}
                margin="normal"
              />
              
              <TextField
                label="Canva Design URL"
                variant="outlined"
                fullWidth
                value={canvaUrl}
                onChange={handleCanvaUrlChange}
                margin="normal"
                placeholder="https://www.canva.com/design/..."
              />
              
              <Button
                variant="contained"
                color="primary"
                onClick={handleImportFromUrl}
                sx={{ mt: 2 }}
                disabled={isLoading}
              >
                {isLoading ? 'Importing...' : 'Import Design'}
              </Button>
            </Paper>
            
            <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary' }}>
              Enter the full URL to your Canva design to link it to your {category} assets.
            </Typography>
          </Box>
        </TabPanel>
      </DialogContent>
      
      <DialogActions>
        <Typography variant="caption" sx={{ flexGrow: 1, opacity: 0.7 }}>
          Powered by Canva
        </Typography>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
      
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default CanvaEmbeddedPicker;
